// @ts-nocheck
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import {
  getBlockDefinition,
  getTexturePathForFace,
  getTextureTintForFace
} from "../core/blockRegistry.ts";
import { MinecraftOfficialAssetLoader } from "./MinecraftOfficialAssetLoader.ts";
import { MinecraftVanillaModelRenderer } from "./MinecraftVanillaModelRenderer.ts";
import {
  DEFAULT_WALK_PHYSICS,
  resolveWalkMotion
} from "../core/walkPhysics.ts";

function seededNoise(x, y, seed) {
  const value = Math.sin(x * 12.9898 + y * 78.233 + seed * 37.719) * 43758.5453;
  return value - Math.floor(value);
}

function createPixelTexture(definition) {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext("2d");
  const palette = definition.palette || [definition.color];
  const seed = definition.id.split("").reduce(function (total, character) {
    return total + character.charCodeAt(0);
  }, 0);

  for (let y = 0; y < 32; y += 1) {
    for (let x = 0; x < 32; x += 1) {
      const noise = seededNoise(x, y, seed);
      const paletteIndex = Math.min(
        palette.length - 1,
        Math.floor(noise * palette.length)
      );
      context.fillStyle = palette[paletteIndex];
      context.fillRect(x, y, 1, 1);
    }
  }

  if (definition.pattern === "brick" || definition.pattern === "crackedBrick" || definition.pattern === "mossyBrick") {
    context.fillStyle = "rgba(24, 29, 31, 0.34)";
    [0, 8, 16, 24].forEach(function (y) {
      context.fillRect(0, y, 32, 1);
    });
    for (let row = 0; row < 4; row += 1) {
      const offset = row % 2 === 0 ? 0 : 8;
      for (let x = offset; x < 32; x += 16) {
        context.fillRect(x, row * 8, 1, 8);
      }
    }
  }

  if (definition.pattern === "plank") {
    context.fillStyle = "rgba(39, 26, 16, 0.28)";
    [0, 8, 16, 24].forEach(function (y) {
      context.fillRect(0, y, 32, 1);
    });
    context.fillStyle = "rgba(238, 199, 132, 0.09)";
    [5, 14, 26].forEach(function (y) {
      context.fillRect(0, y, 32, 1);
    });
  }

  if (definition.pattern === "log") {
    context.fillStyle = "rgba(24, 17, 12, 0.28)";
    for (let x = 2; x < 32; x += 5) {
      context.fillRect(x, 0, 1, 32);
    }
  }

  if (definition.pattern === "tile") {
    context.fillStyle = "rgba(39, 46, 49, 0.25)";
    context.fillRect(0, 0, 32, 1);
    context.fillRect(0, 16, 32, 1);
    context.fillRect(0, 0, 1, 32);
    context.fillRect(16, 0, 1, 32);
  }

  if (definition.pattern === "glass") {
    context.fillStyle = "rgba(218, 247, 255, 0.28)";
    context.fillRect(2, 2, 28, 1);
    context.fillRect(2, 29, 28, 1);
    context.fillRect(2, 2, 1, 28);
    context.fillRect(29, 2, 1, 28);
    context.fillRect(4, 6, 1, 11);
    context.fillRect(5, 5, 10, 1);
  }

  if (definition.pattern === "leaves") {
    context.clearRect(0, 0, 32, 32);
    for (let y = 0; y < 32; y += 1) {
      for (let x = 0; x < 32; x += 1) {
        if (seededNoise(x, y, seed + 11) > 0.18) {
          const paletteIndex = Math.min(
            palette.length - 1,
            Math.floor(seededNoise(x, y, seed) * palette.length)
          );
          context.fillStyle = palette[paletteIndex];
          context.fillRect(x, y, 1, 1);
        }
      }
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapNearestFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.needsUpdate = true;
  return texture;
}

export class VoxelRenderer {
  constructor(
    containerElement,
    world,
    officialTextureLoader = new MinecraftOfficialAssetLoader()
  ) {
    this.containerElement = containerElement;
    this.world = world;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#0c1727");
    this.scene.fog = new THREE.Fog("#0c1727", 72, 180);

    this.camera = new THREE.PerspectiveCamera(52, 1, 0.1, 500);
    this.camera.position.set(54, 39, 58);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance"
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.containerElement.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.set(0, 10, 0);
    this.controls.minDistance = 4;
    this.controls.maxDistance = 190;
    this.controls.maxPolarAngle = Math.PI * 0.495;

    this.worldGroup = new THREE.Group();
    this.scene.add(this.worldGroup);

    this.materialCache = new Map();
    this.officialTextureLoader = officialTextureLoader;
    this.vanillaModelRenderer = new MinecraftVanillaModelRenderer(
      this.officialTextureLoader
    );
    this.vanillaModelsReady = false;
    this.rebuildToken = 0;
    this.baseGeometry = new THREE.BoxGeometry(0.98, 0.98, 0.98);
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.visibleMaximumY = 64;
    this.selection = null;
    this.hover = null;
    this.pointerMoveListener = null;
    this.pointerClickListener = null;
    this.pointerDragListener = null;
    this.selectionDragEnabled = false;
    this.selectionDragStartHit = null;
    this.selectionDragLastHit = null;
    this.selectionDragPointerId = null;
    this.lastPointerDown = null;

    this.walkModeEnabled = false;
    this.walkKeys = new Set();
    this.walkFeetPosition = new THREE.Vector3();
    this.walkVelocity = new THREE.Vector3();
    this.walkYaw = 0;
    this.walkPitch = 0;
    this.walkGrounded = false;
    this.walkStatusListener = null;
    this.walkStatusLastUpdate = 0;
    this.walkPhysics = DEFAULT_WALK_PHYSICS;
    this.defaultCameraFov = 52;
    this.walkCameraFov = 72;
    this.clock = new THREE.Clock();

    this.createEnvironment();
    this.createSelectionHelpers();
    this.attachPointerEvents();
    this.attachWalkModeEvents();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.containerElement);
    this.resize();

    this.worldUnsubscribe = this.world.subscribe(() => this.rebuildWorld());
    this.rebuildWorld();
    this.animate();
  }

  createEnvironment() {
    const hemisphere = new THREE.HemisphereLight("#a7cbe1", "#23372d", 1.55);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight("#ffe4b2", 2.7);
    sun.position.set(-48, 74, 35);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -70;
    sun.shadow.camera.right = 70;
    sun.shadow.camera.top = 70;
    sun.shadow.camera.bottom = -70;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 180;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun);

    const fillLight = new THREE.DirectionalLight("#77a8d5", 0.72);
    fillLight.position.set(35, 30, -45);
    this.scene.add(fillLight);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(220, 220),
      new THREE.MeshStandardMaterial({
        color: "#172a28",
        roughness: 1,
        metalness: 0
      })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.52;
    ground.receiveShadow = true;
    this.ground = ground;
    this.scene.add(ground);

    const grid = new THREE.GridHelper(220, 220, "#426a68", "#253f45");
    grid.position.y = -0.505;
    grid.material.transparent = true;
    grid.material.opacity = 0.28;
    this.scene.add(grid);
  }

  createSelectionHelpers() {
    const selectionGeometry = new THREE.BoxGeometry(1, 1, 1);

    this.selectionMesh = new THREE.Mesh(
      selectionGeometry,
      new THREE.MeshBasicMaterial({
        color: "#9ce7c6",
        wireframe: true,
        transparent: true,
        opacity: 0.95,
        depthTest: false
      })
    );
    this.selectionMesh.renderOrder = 10;
    this.selectionMesh.visible = false;
    this.scene.add(this.selectionMesh);

    this.hoverMesh = new THREE.Mesh(
      new THREE.BoxGeometry(1.025, 1.025, 1.025),
      new THREE.MeshBasicMaterial({
        color: "#8fc9ef",
        wireframe: true,
        transparent: true,
        opacity: 0.56,
        depthTest: false
      })
    );
    this.hoverMesh.renderOrder = 9;
    this.hoverMesh.visible = false;
    this.scene.add(this.hoverMesh);
  }

  setTextureStatusListener(listener) {
    this.officialTextureLoader.setStatusListener(listener);
    this.officialTextureLoader
      .initializeModelLibrary()
      .then(() => {
        this.vanillaModelsReady = true;
        this.rebuildWorld();
      })
      .catch((error) => {
        this.vanillaModelsReady = false;
        this.officialTextureLoader.reportStatus(
          "error",
          "Official vanilla models unavailable · cube fallback active"
        );
        console.warn("Minecraft vanilla model loading failed.", error);
      });
  }

  createMaterial(blockId) {
    if (this.materialCache.has(blockId)) {
      return this.materialCache.get(blockId);
    }

    const definition = getBlockDefinition(blockId);
    const fallbackTexture = createPixelTexture(definition);
    const faceNames = ["side", "side", "top", "bottom", "side", "side"];

    const materials = faceNames.map((faceName) => {
      const material = new THREE.MeshStandardMaterial({
        color: "#ffffff",
        map: fallbackTexture,
        roughness: definition.pattern === "metal" ? 0.52 : 0.9,
        metalness: definition.pattern === "metal" ? 0.22 : 0.02,
        transparent: Boolean(definition.transparent),
        opacity: definition.opacity === undefined ? 1 : definition.opacity,
        alphaTest: definition.alphaTest === undefined
          ? (definition.pattern === "leaves" ? 0.15 : 0)
          : definition.alphaTest,
        depthWrite: !definition.transparent || (definition.opacity || 1) >= 1,
        emissive: definition.emissive || "#000000",
        emissiveIntensity: definition.emissiveIntensity || 0
      });

      const officialTexturePath = getTexturePathForFace(definition, faceName);
      if (officialTexturePath) {
        this.officialTextureLoader
          .loadTexture(officialTexturePath)
          .then((officialTexture) => {
            material.map = officialTexture;
            material.color.set(getTextureTintForFace(definition, faceName));
            material.needsUpdate = true;
          })
          .catch((error) => {
            console.warn(
              "Falling back to generated preview texture for " +
              blockId +
              " (" +
              officialTexturePath +
              ").",
              error
            );
          });
      }

      return material;
    });

    this.materialCache.set(blockId, materials);
    return materials;
  }

  clearWorldMeshes() {
    while (this.worldGroup.children.length > 0) {
      this.worldGroup.remove(this.worldGroup.children[0]);
    }
  }

  rebuildWorld() {
    const token = ++this.rebuildToken;
    const visibleEntries = this.world.entries().filter((entry) => {
      return entry.y <= this.visibleMaximumY;
    });

    if (!this.vanillaModelsReady) {
      this.rebuildFallbackWorld(visibleEntries);
      return;
    }

    this.rebuildVanillaWorld(visibleEntries, token).catch((error) => {
      if (token !== this.rebuildToken) {
        return;
      }
      console.warn(
        "Vanilla model rebuild failed; using cube fallback.",
        error
      );
      this.rebuildFallbackWorld(visibleEntries);
    });
  }

  rebuildFallbackWorld(entries) {
    this.clearWorldMeshes();

    const groupedBlocks = new Map();
    entries.forEach((entry) => {
      const key = entry.blockSpec || entry.blockId;
      if (!groupedBlocks.has(key)) {
        groupedBlocks.set(key, []);
      }
      groupedBlocks.get(key).push(entry);
    });

    const matrix = new THREE.Matrix4();

    groupedBlocks.forEach((blocks) => {
      const blockId = blocks[0].blockId;
      const mesh = new THREE.InstancedMesh(
        this.baseGeometry,
        this.createMaterial(blockId),
        blocks.length
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.blockId = blockId;
      mesh.userData.instancePositions = [];

      blocks.forEach(function (block, index) {
        matrix.makeTranslation(block.x, block.y, block.z);
        mesh.setMatrixAt(index, matrix);
        mesh.userData.instancePositions[index] = {
          x: block.x,
          y: block.y,
          z: block.z,
          blockId: block.blockId,
          blockSpec: block.blockSpec,
          state: block.state
        };
      });

      mesh.instanceMatrix.needsUpdate = true;
      this.worldGroup.add(mesh);
    });
  }

  async rebuildVanillaWorld(entries, token) {
    const groupedParts = new Map();
    const fallbackEntries = [];

    const batchSize = 192;

    for (
      let batchStart = 0;
      batchStart < entries.length;
      batchStart += batchSize
    ) {
      if (token !== this.rebuildToken) {
        return;
      }

      const batch = entries.slice(
        batchStart,
        batchStart + batchSize
      );

      const resolvedBatch = await Promise.all(
        batch.map(async (entry) => {
          try {
            const parts =
              await this.vanillaModelRenderer.createRenderParts(entry);
            return { entry, parts, error: null };
          } catch (error) {
            return { entry, parts: [], error };
          }
        })
      );

      if (token !== this.rebuildToken) {
        return;
      }

      resolvedBatch.forEach(function (resolved) {
        const entry = resolved.entry;

        if (resolved.error || !resolved.parts.length) {
          fallbackEntries.push(entry);
          if (resolved.error) {
            console.warn(
              "Using cube fallback for " +
                entry.blockSpec +
                ".",
              resolved.error
            );
          }
          return;
        }

        resolved.parts.forEach(function (bundle) {
          if (!groupedParts.has(bundle.signature)) {
            groupedParts.set(bundle.signature, {
              bundle,
              blocks: []
            });
          }
          groupedParts.get(bundle.signature).blocks.push(entry);
        });
      });

      await new Promise(function (resolve) {
        requestAnimationFrame(resolve);
      });
    }

    if (token !== this.rebuildToken) {
      return;
    }

    this.clearWorldMeshes();
    const matrix = new THREE.Matrix4();

    groupedParts.forEach((group) => {
      const mesh = new THREE.InstancedMesh(
        group.bundle.geometry,
        group.bundle.materials,
        group.blocks.length
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.instancePositions = [];

      group.blocks.forEach(function (block, index) {
        matrix.makeTranslation(block.x, block.y, block.z);
        mesh.setMatrixAt(index, matrix);
        mesh.userData.instancePositions[index] = {
          x: block.x,
          y: block.y,
          z: block.z,
          blockId: block.blockId,
          blockSpec: block.blockSpec,
          state: block.state
        };
      });

      mesh.instanceMatrix.needsUpdate = true;
      this.worldGroup.add(mesh);
    });

    if (fallbackEntries.length > 0) {
      const groupedFallback = new Map();
      fallbackEntries.forEach(function (entry) {
        const key = entry.blockSpec || entry.blockId;
        if (!groupedFallback.has(key)) {
          groupedFallback.set(key, []);
        }
        groupedFallback.get(key).push(entry);
      });

      groupedFallback.forEach((blocks) => {
        const blockId = blocks[0].blockId;
        const mesh = new THREE.InstancedMesh(
          this.baseGeometry,
          this.createMaterial(blockId),
          blocks.length
        );
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.instancePositions = [];

        blocks.forEach(function (block, index) {
          matrix.makeTranslation(block.x, block.y, block.z);
          mesh.setMatrixAt(index, matrix);
          mesh.userData.instancePositions[index] = {
            x: block.x,
            y: block.y,
            z: block.z,
            blockId: block.blockId,
            blockSpec: block.blockSpec,
            state: block.state
          };
        });

        mesh.instanceMatrix.needsUpdate = true;
        this.worldGroup.add(mesh);
      });
    }
  }

  setVisibleMaximumY(y) {
    this.visibleMaximumY = Number(y);
    this.rebuildWorld();
  }

  setSelection(position) {
    this.setSelectionRegion(position, position);
  }

  setSelectionRegion(start, end = start) {
    this.selection = start
      ? {
          start: { ...start },
          end: end ? { ...end } : { ...start }
        }
      : null;
    this.selectionMesh.visible = Boolean(start);

    if (!start) {
      return;
    }

    const finalEnd = end || start;
    const min = {
      x: Math.min(start.x, finalEnd.x),
      y: Math.min(start.y, finalEnd.y),
      z: Math.min(start.z, finalEnd.z)
    };
    const max = {
      x: Math.max(start.x, finalEnd.x),
      y: Math.max(start.y, finalEnd.y),
      z: Math.max(start.z, finalEnd.z)
    };
    const size = {
      x: max.x - min.x + 1,
      y: max.y - min.y + 1,
      z: max.z - min.z + 1
    };

    this.selectionMesh.position.set(
      (min.x + max.x) / 2,
      (min.y + max.y) / 2,
      (min.z + max.z) / 2
    );
    this.selectionMesh.scale.set(
      size.x + 0.04,
      size.y + 0.04,
      size.z + 0.04
    );
  }

  setHover(position) {
    this.hover = position ? { ...position } : null;
    this.hoverMesh.visible = Boolean(position);
    if (position) {
      this.hoverMesh.position.set(position.x, position.y, position.z);
    }
  }

  setPointerMoveListener(listener) {
    this.pointerMoveListener = listener;
  }

  setPointerClickListener(listener) {
    this.pointerClickListener = listener;
  }

  setPointerDragListener(listener) {
    this.pointerDragListener = listener;
  }

  setSelectionDragEnabled(enabled) {
    this.selectionDragEnabled = Boolean(enabled);
    if (!this.selectionDragEnabled) {
      this.selectionDragStartHit = null;
      this.selectionDragLastHit = null;
      this.selectionDragPointerId = null;
      this.controls.enabled = !this.walkModeEnabled;
    }
  }

  finishSelectionDrag(event, phase = "end") {
    if (!this.selectionDragStartHit) {
      return false;
    }

    const endHit = this.pick(event) || this.selectionDragLastHit;
    if (endHit && this.pointerDragListener) {
      this.pointerDragListener(
        this.selectionDragStartHit,
        endHit,
        phase,
        event
      );
    }

    if (
      this.selectionDragPointerId !== null &&
      this.renderer.domElement.hasPointerCapture?.(
        this.selectionDragPointerId
      )
    ) {
      this.renderer.domElement.releasePointerCapture(
        this.selectionDragPointerId
      );
    }

    this.selectionDragStartHit = null;
    this.selectionDragLastHit = null;
    this.selectionDragPointerId = null;
    this.lastPointerDown = null;
    this.controls.enabled = !this.walkModeEnabled;
    return true;
  }

  attachPointerEvents() {
    this.renderer.domElement.addEventListener(
      "pointerdown",
      (event) => {
        if (this.walkModeEnabled) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.lastPointerDown = null;
          if (
            document.pointerLockElement !==
            this.renderer.domElement
          ) {
            this.requestWalkPointerLock();
          }
          return;
        }

        this.lastPointerDown = {
          x: event.clientX,
          y: event.clientY
        };

        const startSelectionDrag =
          this.selectionDragEnabled &&
          event.button === 0 &&
          event.shiftKey;

        if (!startSelectionDrag) {
          return;
        }

        const startHit = this.pick(event);
        if (!startHit) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.controls.enabled = false;
        this.selectionDragStartHit = startHit;
        this.selectionDragLastHit = startHit;
        this.selectionDragPointerId = event.pointerId;
        this.renderer.domElement.setPointerCapture?.(
          event.pointerId
        );

        if (this.pointerDragListener) {
          this.pointerDragListener(
            startHit,
            startHit,
            "start",
            event
          );
        }
      },
      true
    );

    this.renderer.domElement.addEventListener("pointermove", (event) => {
      if (this.walkModeEnabled) {
        this.setHover(null);
        return;
      }

      const hit = this.pick(event);
      this.setHover(
        hit ? (hit.isGround ? hit.adjacent : hit.position) : null
      );

      if (this.selectionDragStartHit) {
        event.preventDefault();
        if (hit) {
          this.selectionDragLastHit = hit;
          if (this.pointerDragListener) {
            this.pointerDragListener(
              this.selectionDragStartHit,
              hit,
              "move",
              event
            );
          }
        }
        if (this.pointerMoveListener) {
          this.pointerMoveListener(hit, event);
        }
        return;
      }

      if (this.pointerMoveListener) {
        this.pointerMoveListener(hit, event);
      }
    });

    this.renderer.domElement.addEventListener("pointerleave", () => {
      if (this.walkModeEnabled) {
        return;
      }
      if (this.selectionDragStartHit) {
        return;
      }
      this.setHover(null);
      if (this.pointerMoveListener) {
        this.pointerMoveListener(null, null);
      }
    });

    this.renderer.domElement.addEventListener("pointerup", (event) => {
      if (this.walkModeEnabled) {
        this.lastPointerDown = null;
        return;
      }
      if (this.selectionDragStartHit) {
        event.preventDefault();
        this.finishSelectionDrag(event, "end");
        return;
      }

      if (!this.lastPointerDown) {
        return;
      }

      const movedDistance = Math.hypot(
        event.clientX - this.lastPointerDown.x,
        event.clientY - this.lastPointerDown.y
      );
      this.lastPointerDown = null;

      if (movedDistance > 5) {
        return;
      }

      const hit = this.pick(event);
      if (this.pointerClickListener) {
        this.pointerClickListener(hit, event);
      }
    });

    this.renderer.domElement.addEventListener(
      "pointercancel",
      (event) => {
        if (this.selectionDragStartHit) {
          this.finishSelectionDrag(event, "end");
        }
      }
    );
  }

  pick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const intersections = this.raycaster.intersectObjects(this.worldGroup.children, false);
    if (intersections.length === 0) {
      const groundIntersections = this.raycaster.intersectObject(this.ground, false);
      if (groundIntersections.length === 0) {
        return null;
      }

      const groundPoint = groundIntersections[0].point;
      const groundX = Math.round(groundPoint.x);
      const groundZ = Math.round(groundPoint.z);

      return {
        position: {
          x: groundX,
          y: -1,
          z: groundZ,
          blockId: null
        },
        normal: { x: 0, y: 1, z: 0 },
        adjacent: { x: groundX, y: 0, z: groundZ },
        blockId: null,
        isGround: true
      };
    }

    const intersection = intersections[0];
    const mesh = intersection.object;
    const position = mesh.userData.instancePositions[intersection.instanceId];
    if (!position) {
      return null;
    }

    const normal = intersection.face
      ? intersection.face.normal.clone()
      : new THREE.Vector3(0, 1, 0);

    return {
      position: { ...position },
      normal: {
        x: Math.round(normal.x),
        y: Math.round(normal.y),
        z: Math.round(normal.z)
      },
      adjacent: {
        x: position.x + Math.round(normal.x),
        y: position.y + Math.round(normal.y),
        z: position.z + Math.round(normal.z)
      },
      blockId: position.blockId,
      blockSpec: position.blockSpec,
      state: position.state || {}
    };
  }

  frameWorld() {
    const bounds = this.world.getBounds();
    const center = new THREE.Vector3(
      (bounds.min.x + bounds.max.x) / 2,
      (bounds.min.y + bounds.max.y) / 2,
      (bounds.min.z + bounds.max.z) / 2
    );
    const size = new THREE.Vector3(
      bounds.max.x - bounds.min.x + 1,
      bounds.max.y - bounds.min.y + 1,
      bounds.max.z - bounds.min.z + 1
    );
    const radius = Math.max(size.x, size.y, size.z, 12);
    this.controls.target.copy(center);
    this.camera.position.set(
      center.x + radius * 1.18,
      center.y + radius * 0.82,
      center.z + radius * 1.24
    );
    this.camera.lookAt(center);
    this.controls.update();
  }

  setCameraView(viewName) {
    const bounds = this.world.getBounds();
    const center = new THREE.Vector3(
      (bounds.min.x + bounds.max.x) / 2,
      (bounds.min.y + bounds.max.y) / 2,
      (bounds.min.z + bounds.max.z) / 2
    );
    const extent = Math.max(
      bounds.max.x - bounds.min.x,
      bounds.max.y - bounds.min.y,
      bounds.max.z - bounds.min.z,
      18
    );

    this.controls.target.copy(center);

    if (viewName === "top") {
      this.camera.position.set(center.x + 0.01, center.y + extent * 1.9, center.z + 0.01);
      this.camera.up.set(0, 0, -1);
    } else if (viewName === "front") {
      this.camera.position.set(center.x, center.y + extent * 0.25, center.z + extent * 1.8);
      this.camera.up.set(0, 1, 0);
    } else {
      this.camera.position.set(
        center.x + extent * 1.12,
        center.y + extent * 0.78,
        center.z + extent * 1.18
      );
      this.camera.up.set(0, 1, 0);
    }

    this.camera.lookAt(center);
    this.controls.update();
  }

  setWalkStatusListener(listener) {
    this.walkStatusListener = listener;
    this.emitWalkStatus(true);
  }

  emitWalkStatus(force = false) {
    if (!this.walkStatusListener) {
      return;
    }

    const now = performance.now();
    if (!force && now - this.walkStatusLastUpdate < 100) {
      return;
    }
    this.walkStatusLastUpdate = now;

    this.walkStatusListener({
      enabled: this.walkModeEnabled,
      pointerLocked:
        document.pointerLockElement === this.renderer.domElement,
      grounded: this.walkGrounded,
      position: {
        x: this.walkFeetPosition.x,
        y: this.walkFeetPosition.y,
        z: this.walkFeetPosition.z
      }
    });
  }

  requestWalkPointerLock() {
    if (!this.walkModeEnabled) {
      return;
    }

    const requestResult =
      this.renderer.domElement.requestPointerLock?.();
    if (
      requestResult &&
      typeof requestResult.catch === "function"
    ) {
      requestResult.catch(() => {
        this.emitWalkStatus(true);
      });
    }
  }

  enterWalkMode() {
    if (this.walkModeEnabled) {
      this.requestWalkPointerLock();
      return;
    }

    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    this.walkYaw = Math.atan2(-forward.x, -forward.z);
    this.walkPitch = Math.asin(
      THREE.MathUtils.clamp(forward.y, -1, 1)
    );

    this.walkFeetPosition.set(
      this.camera.position.x,
      Math.max(
        this.walkPhysics.groundLevel,
        this.camera.position.y - this.walkPhysics.eyeHeight
      ),
      this.camera.position.z
    );
    this.walkVelocity.set(0, 0, 0);
    this.walkGrounded = false;
    this.walkKeys.clear();
    this.walkModeEnabled = true;

    this.controls.enabled = false;
    this.setHover(null);
    this.selectionMesh.visible = false;
    this.camera.up.set(0, 1, 0);
    this.camera.fov = this.walkCameraFov;
    this.camera.updateProjectionMatrix();
    this.applyWalkCamera();
    this.emitWalkStatus(true);
    this.requestWalkPointerLock();
  }

  exitWalkMode() {
    if (!this.walkModeEnabled) {
      return;
    }

    if (
      document.pointerLockElement ===
      this.renderer.domElement
    ) {
      document.exitPointerLock?.();
    }

    this.walkModeEnabled = false;
    this.walkKeys.clear();
    this.walkVelocity.set(0, 0, 0);
    this.camera.fov = this.defaultCameraFov;
    this.camera.updateProjectionMatrix();
    this.camera.up.set(0, 1, 0);

    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    this.controls.target.copy(
      this.camera.position.clone().addScaledVector(
        forward,
        8
      )
    );
    this.controls.enabled = true;
    this.controls.update();

    if (this.selection) {
      this.setSelectionRegion(
        this.selection.start,
        this.selection.end
      );
    }

    this.emitWalkStatus(true);
  }

  attachWalkModeEvents() {
    this.walkKeyDownHandler = (event) => {
      if (
        !this.walkModeEnabled ||
        document.pointerLockElement !==
          this.renderer.domElement
      ) {
        return;
      }

      const movementCodes = new Set([
        "KeyW",
        "KeyA",
        "KeyS",
        "KeyD",
        "ShiftLeft",
        "ShiftRight",
        "Space"
      ]);
      if (!movementCodes.has(event.code)) {
        return;
      }

      event.preventDefault();
      this.walkKeys.add(event.code);

      if (
        event.code === "Space" &&
        !event.repeat &&
        this.walkGrounded
      ) {
        this.walkVelocity.y =
          this.walkPhysics.jumpSpeed;
        this.walkGrounded = false;
      }
    };

    this.walkKeyUpHandler = (event) => {
      this.walkKeys.delete(event.code);
    };

    this.walkMouseMoveHandler = (event) => {
      if (
        !this.walkModeEnabled ||
        document.pointerLockElement !==
          this.renderer.domElement
      ) {
        return;
      }

      const sensitivity = 0.00215;
      this.walkYaw -= event.movementX * sensitivity;
      this.walkPitch -= event.movementY * sensitivity;
      this.walkPitch = THREE.MathUtils.clamp(
        this.walkPitch,
        -Math.PI * 0.495,
        Math.PI * 0.495
      );
      this.applyWalkCamera();
    };

    this.walkPointerLockHandler = () => {
      if (
        document.pointerLockElement !==
        this.renderer.domElement
      ) {
        this.walkKeys.clear();
      }
      this.emitWalkStatus(true);
    };

    this.walkWindowBlurHandler = () => {
      this.walkKeys.clear();
    };

    window.addEventListener(
      "keydown",
      this.walkKeyDownHandler
    );
    window.addEventListener(
      "keyup",
      this.walkKeyUpHandler
    );
    window.addEventListener(
      "blur",
      this.walkWindowBlurHandler
    );
    document.addEventListener(
      "mousemove",
      this.walkMouseMoveHandler
    );
    document.addEventListener(
      "pointerlockchange",
      this.walkPointerLockHandler
    );
  }

  applyWalkCamera() {
    if (!this.walkModeEnabled) {
      return;
    }

    this.camera.position.set(
      this.walkFeetPosition.x,
      this.walkFeetPosition.y +
        this.walkPhysics.eyeHeight,
      this.walkFeetPosition.z
    );
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.set(
      this.walkPitch,
      this.walkYaw,
      0
    );
  }

  updateWalkMode(deltaSeconds) {
    if (!this.walkModeEnabled) {
      return;
    }

    if (
      document.pointerLockElement !==
      this.renderer.domElement
    ) {
      this.applyWalkCamera();
      this.emitWalkStatus();
      return;
    }

    const delta = Math.min(
      Math.max(deltaSeconds, 0),
      0.05
    );
    const forwardInput =
      (this.walkKeys.has("KeyW") ? 1 : 0) -
      (this.walkKeys.has("KeyS") ? 1 : 0);
    const sideInput =
      (this.walkKeys.has("KeyD") ? 1 : 0) -
      (this.walkKeys.has("KeyA") ? 1 : 0);

    const inputLength = Math.hypot(
      forwardInput,
      sideInput
    );
    const sprinting =
      this.walkKeys.has("ShiftLeft") ||
      this.walkKeys.has("ShiftRight");
    const speed = sprinting
      ? this.walkPhysics.sprintSpeed
      : this.walkPhysics.walkSpeed;

    let movementX = 0;
    let movementZ = 0;
    if (inputLength > 0) {
      const normalizedForward =
        forwardInput / inputLength;
      const normalizedSide =
        sideInput / inputLength;

      const forwardX = -Math.sin(this.walkYaw);
      const forwardZ = -Math.cos(this.walkYaw);
      const rightX = Math.cos(this.walkYaw);
      const rightZ = -Math.sin(this.walkYaw);

      movementX =
        (
          forwardX * normalizedForward +
          rightX * normalizedSide
        ) *
        speed *
        delta;
      movementZ =
        (
          forwardZ * normalizedForward +
          rightZ * normalizedSide
        ) *
        speed *
        delta;
    }

    this.walkVelocity.y -=
      this.walkPhysics.gravity * delta;

    const result = resolveWalkMotion(
      this.world,
      {
        x: this.walkFeetPosition.x,
        y: this.walkFeetPosition.y,
        z: this.walkFeetPosition.z
      },
      {
        x: movementX,
        y: this.walkVelocity.y * delta,
        z: movementZ
      },
      this.walkPhysics
    );

    this.walkFeetPosition.set(
      result.position.x,
      result.position.y,
      result.position.z
    );
    this.walkGrounded = result.grounded;

    if (result.collidedY) {
      this.walkVelocity.y = 0;
    }

    this.applyWalkCamera();
    this.emitWalkStatus();
  }

  resize() {
    const width = Math.max(1, this.containerElement.clientWidth);
    const height = Math.max(1, this.containerElement.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  animate() {
    this.animationFrame = requestAnimationFrame(() => this.animate());
    const delta = this.clock.getDelta();

    if (this.walkModeEnabled) {
      this.updateWalkMode(delta);
    } else {
      this.controls.update();
    }

    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    cancelAnimationFrame(this.animationFrame);
    this.walkStatusListener = null;

    if (
      document.pointerLockElement ===
      this.renderer.domElement
    ) {
      document.exitPointerLock?.();
    }

    window.removeEventListener(
      "keydown",
      this.walkKeyDownHandler
    );
    window.removeEventListener(
      "keyup",
      this.walkKeyUpHandler
    );
    window.removeEventListener(
      "blur",
      this.walkWindowBlurHandler
    );
    document.removeEventListener(
      "mousemove",
      this.walkMouseMoveHandler
    );
    document.removeEventListener(
      "pointerlockchange",
      this.walkPointerLockHandler
    );

    this.resizeObserver.disconnect();
    this.worldUnsubscribe();
    this.materialCache.forEach(function (materials) {
      materials.forEach(function (material) {
        material.dispose();
      });
    });
    this.vanillaModelRenderer.dispose();
    this.baseGeometry.dispose();
    this.renderer.dispose();
  }
}
